/**
 * Terms of use — the document behind the "használati feltételek" link on the
 * sign-in page and the footer. Plain data (no React) so the node-env content
 * test can check both translations without a renderer; the page in
 * `pages/Terms.tsx` renders whichever language the i18n toggle selects.
 *
 * Keep HU and EN structurally identical (same sections, same block order,
 * same bullet counts) — `terms.test.ts` pins that, so a paragraph dropped
 * from one translation fails the build instead of shipping silently.
 *
 * Facts the wording relies on (verify before changing them):
 *   - sign-in is allowlisted + magic-link (partner-portal-api handler.ts)
 *   - demo pass records expire 14 days after mint (handler.ts, expiresAt)
 *   - e-mail + IP rate limiting on the magic-link endpoint (handler.ts)
 *   - hosting: AWS eu-central-1 (README), passes via Apple/Google Wallet
 */
import type { Lang } from '../i18n/messages';

export type TermsBlock = { kind: 'p'; text: string } | { kind: 'list'; items: string[] };

export interface TermsSection {
  heading: string;
  body: TermsBlock[];
}

export interface TermsDocument {
  title: string;
  /** The "effective from" line shown under the title. */
  effective: string;
  intro: string;
  sections: TermsSection[];
}

export const TERMS_VERSION = '1.0';
/** ISO date of the current version — bump together with `effective` below. */
export const TERMS_EFFECTIVE_DATE = '2026-09-07';

const p = (text: string): TermsBlock => ({ kind: 'p', text });
const list = (...items: string[]): TermsBlock => ({ kind: 'list', items });

const hu: TermsDocument = {
  title: 'Használati feltételek',
  effective: `Hatályos: 2026. szeptember 7. (${TERMS_VERSION} verzió)`,
  intro:
    'Ez a dokumentum az Insurwiz Wallet demo környezet (a továbbiakban: Demo) használatának feltételeit tartalmazza. A Demóba történő bejelentkezéssel a felhasználó — és az általa képviselt szervezet — elfogadja az alábbi feltételeket. Ha nem ért egyet velük, kérjük, ne használja a Demót.',
  sections: [
    {
      heading: '1. A szolgáltató és a dokumentum hatálya',
      body: [
        p(
          'A Demót az Insurwiz Technology Kft. (a továbbiakban: Insurwiz) üzemelteti. Kapcsolat: hello@insurwiz.io.',
        ),
        p(
          'A feltételek a wallet-demo.insurwiz.io és a wallet-demo-dev.insurwiz.io címen elérhető partneri portálra, valamint az ott létrehozott Apple Wallet és Google Wallet passokra vonatkoznak. A Demo üzleti partnerek (biztosítók, alkuszok, utazási és assistance-szolgáltatók, illetve ezek képviselői) számára készült; nem fogyasztóknak szóló szolgáltatás.',
        ),
      ],
    },
    {
      heading: '2. A Demo célja és jellege',
      body: [
        p(
          'A Demo bemutató és kiértékelési környezet: lehetővé teszi, hogy a partner saját márkázással passokat tervezzen és generáljon, majd megnézze, hogyan jelenik meg és frissül egy pass a végfelhasználó telefonján. A Demo béta állapotú, előzetes kiadású funkciókat tartalmaz, amelyek bármikor változhatnak.',
        ),
        p(
          'A Demo nem éles szolgáltatás. Az Insurwiz nem vállal rendelkezésre állási garanciát, és a Demót — beleértve a benne tárolt adatokat és a létrehozott passokat — bármikor módosíthatja, alaphelyzetbe állíthatja, felfüggesztheti vagy megszüntetheti.',
        ),
      ],
    },
    {
      heading: '3. Fiók és belépés',
      body: [
        p(
          'A Demo fiókokat az Insurwiz hozza létre, illetve engedélyezi; belépni csak az Insurwiz által regisztrált e-mail-címmel lehet. A belépés jelszó nélkül, az e-mail-címre küldött egyszer használatos, rövid ideig érvényes belépési linkkel történik.',
        ),
        list(
          'A felhasználó felel az e-mail-fiókja biztonságáért és minden olyan tevékenységért, amely a belépési linkjével történik.',
          'A belépési linket tilos továbbadni. Ha a felhasználó visszaélést észlel, haladéktalanul jelezze a hello@insurwiz.io címen.',
          'Az egyes sablonokat és funkciókat az Insurwiz fiókonként kapcsolja be; ezek elérhetőségére a felhasználónak nincs alanyi joga.',
        ),
      ],
    },
    {
      heading: '4. Elfogadható használat',
      body: [
        p(
          'A Demo kizárólag a szolgáltatás kiértékelésére és bemutatására használható. A felhasználó vállalja, hogy:',
        ),
        list(
          'csak tesztadatokat, saját adatait vagy olyan személyek adatait rögzíti, akiknek az adatait jogszerűen, megfelelő jogalappal és tájékoztatás mellett használhatja;',
          'nem generál passt olyan személynek, aki azt nem kérte vagy nem várja, és nem használja a Demót kéretlen üzenetek küldésére;',
          'nem tölt fel jogsértő, megtévesztő, sértő vagy harmadik fél jogait sértő tartalmat (logót, szöveget, képet);',
          'nem kísérli meg a Demo biztonsági intézkedéseinek megkerülését, a rendszer terheléses tesztelését, automatizált tömeges használatát, visszafejtését vagy más felhasználók adataihoz való hozzáférést;',
          'nem használja a Demót a hatályos jogszabályokba ütköző módon.',
        ),
        p(
          'A szabályok megsértése esetén az Insurwiz a fiókot előzetes értesítés nélkül felfüggesztheti vagy törölheti, és a létrehozott passokat érvénytelenítheti.',
        ),
      ],
    },
    {
      heading: '5. A passok és a járatinformációk',
      body: [
        p(
          'A Demóban létrehozott passok kizárólag bemutató célt szolgálnak. Nem minősülnek biztosítási szerződésnek, kötvénynek, fedezetigazolásnak, jegynek, belépési jogosultságnak vagy bármilyen szolgáltatásra való jogosultságnak, és nem keletkeztetnek követelést sem az Insurwizzel, sem a partnerrel, sem harmadik féllel szemben.',
        ),
        p(
          'A passokon és az értesítésekben megjelenő járatinformációk (indulás, érkezés, kapu, terminál, késés, státusz) külső járatadat-szolgáltatóktól származnak, és tájékoztató jellegűek. Késhetnek, hiányosak vagy pontatlanok lehetnek; utazási döntést kizárólag a légitársaság és a repülőtér hivatalos tájékoztatása alapján szabad hozni.',
        ),
        p(
          'A demo passok korlátozott ideig érvényesek: a pass rekordja a létrehozástól számított 14 nap elteltével lejár, a passon beállított láthatósági ablak pedig ennél rövidebb is lehet. A lejárt vagy törölt passok a Walletben inaktívvá válhatnak.',
        ),
      ],
    },
    {
      heading: '6. Harmadik felek szolgáltatásai',
      body: [
        p(
          'A passok kézbesítése és megjelenítése az Apple Wallet (Apple Inc.) és a Google Wallet (Google LLC) szolgáltatásain keresztül történik, a push-értesítéseket az Apple és a Google értesítési szolgáltatásai továbbítják. Ezek használatára az adott szolgáltató saját feltételei is vonatkoznak, és az Insurwiz nem felel ezek működéséért, elérhetőségéért vagy változásáért.',
        ),
        p(
          'A Demót az Insurwiz az Amazon Web Services (AWS) infrastruktúráján, az Európai Unióban (Frankfurt, eu-central-1 régió) üzemelteti. A belépési linkeket e-mail-kézbesítő szolgáltatáson keresztül küldjük, a járatadatokat külső járatadat-szolgáltatóktól szerezzük be.',
        ),
      ],
    },
    {
      heading: '7. Szellemi tulajdon és a partner tartalmai',
      body: [
        p(
          'A Demo, annak szoftvere, felülete, sablonjai, mintatervei és dokumentációja az Insurwiz szellemi tulajdona; a Demo használata ezekre semmilyen jogot nem ruház át. A felhasználó a Demót kizárólag a jelen feltételek szerinti kiértékelésre használhatja.',
        ),
        p(
          'A partner által feltöltött logó, márkaszínek, szövegek és egyéb tartalmak a partner (vagy jogosultja) tulajdonában maradnak. A feltöltéssel a partner szavatolja, hogy jogosult ezek használatára, és nem kizárólagos, díjmentes engedélyt ad az Insurwiznek arra, hogy a tartalmakat a Demo működtetése céljából tárolja, feldolgozza, a passokon megjelenítse és az Apple, illetve a Google szolgáltatásainak továbbítsa.',
        ),
        p(
          'A Demóval kapcsolatos észrevételeket és javaslatokat az Insurwiz szabadon, ellentételezés nélkül felhasználhatja a szolgáltatás fejlesztésére.',
        ),
      ],
    },
    {
      heading: '8. Adatkezelés',
      body: [
        p(
          'A Demo működtetése során az Insurwiz a következő adatokat kezeli: a felhasználó e-mail-címe, neve és cégneve; a partner márkabeállításai (logó, színek, kapcsolati adatok, pass-tervek); a passokhoz rögzített címzetti adatok (név, e-mail-cím, járatszám, dátum, indulási és érkezési repülőtér, illetve a sablon egyéb mezői); a járatok státuszadatai; valamint technikai naplóadatok (IP-cím, időbélyegek, kérésazonosítók), amelyeket a biztonság és a visszaélések megelőzése érdekében (például kérésszám-korlátozás) rögzítünk.',
        ),
        p(
          'A felhasználó saját adatai tekintetében az Insurwiz adatkezelő. A partner által rögzített címzetti adatok tekintetében a partner minősül adatkezelőnek, az Insurwiz pedig a partner megbízásából, a Demo működtetése céljából adatfeldolgozóként jár el; a szükséges jogalap és tájékoztatás biztosítása a partner felelőssége.',
        ),
        p(
          'Az adatokat a Demo céljának megvalósulásáig, illetve a fiók megszűnéséig kezeljük; a lejárt demo adatokat rendszeresen töröljük. A Demo nem alkalmas és nem használható éles ügyféladatok tárolására. Az érintetti jogok (hozzáférés, helyesbítés, törlés, korlátozás, tiltakozás) gyakorlásához és minden adatvédelmi kérdésben a gdpr@insurwiz.io címen állunk rendelkezésre.',
        ),
      ],
    },
    {
      heading: '9. Rendelkezésre állás, módosítás és megszüntetés',
      body: [
        p(
          'A Demo „ahogy van” és „ahogy elérhető” alapon vehető igénybe. Az Insurwiz nem vállal kötelezettséget a folyamatos vagy hibamentes működésre, meghatározott rendelkezésre állásra, a funkciók fennmaradására vagy az adatok megőrzésére.',
        ),
        p(
          'Az Insurwiz a Demót vagy annak bármely részét bármikor, előzetes értesítés nélkül módosíthatja, felfüggesztheti vagy megszüntetheti, a fiókokat törölheti és a passokat érvénytelenítheti. A felhasználó a fiókja és adatai törlését bármikor kérheti a hello@insurwiz.io vagy a gdpr@insurwiz.io címen.',
        ),
      ],
    },
    {
      heading: '10. Felelősség',
      body: [
        p(
          'A Demo bemutató jellegére tekintettel az Insurwiz — a jogszabály által megengedett legteljesebb mértékben — kizárja a felelősségét a Demo használatából vagy használhatatlanságából, a benne megjelenő információk (ideértve a járatadatokat) pontatlanságából, valamint harmadik felek szolgáltatásainak hibáiból eredő károkért, ideértve az elmaradt hasznot és a következményi károkat is.',
        ),
        p(
          'A felelősségkorlátozás nem terjed ki a szándékosan okozott, továbbá az emberi életet, testi épséget vagy egészséget megkárosító károkozásért való felelősségre, és azokra az esetekre, amelyekben a felelősség kizárását vagy korlátozását jogszabály nem engedi.',
        ),
        p(
          'A felhasználó, illetve az általa képviselt partner felel az általa rögzített adatokért és feltöltött tartalmakért, valamint a jelen feltételek megsértéséből eredő károkért és igényekért.',
        ),
      ],
    },
    {
      heading: '11. A feltételek módosítása',
      body: [
        p(
          'Az Insurwiz a jelen feltételeket bármikor módosíthatja. A mindenkor hatályos változat ezen az oldalon érhető el, a hatálybalépés dátumával együtt. A módosítást követő bejelentkezés vagy további használat a módosított feltételek elfogadásának minősül.',
        ),
      ],
    },
    {
      heading: '12. Alkalmazandó jog, nyelv és kapcsolat',
      body: [
        p(
          'A jelen feltételekre a magyar jog az irányadó. A feltételekből eredő jogvitákra a hatáskörrel és illetékességgel rendelkező magyar bíróságok járnak el. A feltételek magyar és angol nyelven érhetők el; eltérés esetén a magyar nyelvű változat az irányadó.',
        ),
        p(
          'Kérdés, észrevétel vagy jogi megkeresés esetén: hello@insurwiz.io (általános), gdpr@insurwiz.io (adatvédelem), legal@insurwiz.io (jogi ügyek).',
        ),
      ],
    },
  ],
};

const en: TermsDocument = {
  title: 'Terms of Use',
  effective: `Effective: 7 September 2026 (version ${TERMS_VERSION})`,
  intro:
    'This document sets out the terms on which the Insurwiz Wallet demo environment (the “Demo”) may be used. By signing in to the Demo, the user — and the organisation they represent — accepts these terms. If you do not agree with them, please do not use the Demo.',
  sections: [
    {
      heading: '1. Operator and scope',
      body: [
        p(
          'The Demo is operated by Insurwiz Technology Kft. (“Insurwiz”). Contact: hello@insurwiz.io.',
        ),
        p(
          'These terms apply to the partner portal available at wallet-demo.insurwiz.io and wallet-demo-dev.insurwiz.io, and to the Apple Wallet and Google Wallet passes created there. The Demo is intended for business partners (insurers, brokers, travel and assistance providers and their representatives); it is not a consumer service.',
        ),
      ],
    },
    {
      heading: '2. Purpose and nature of the Demo',
      body: [
        p(
          'The Demo is an evaluation and demonstration environment: it lets a partner design and generate passes with their own branding and see how a pass appears and updates on an end user’s phone. The Demo is in beta and contains pre-release functionality that may change at any time.',
        ),
        p(
          'The Demo is not a production service. Insurwiz gives no availability guarantee and may modify, reset, suspend or discontinue the Demo — including the data stored in it and the passes created — at any time.',
        ),
      ],
    },
    {
      heading: '3. Accounts and sign-in',
      body: [
        p(
          'Demo accounts are created and enabled by Insurwiz; only e-mail addresses registered by Insurwiz can sign in. Sign-in is passwordless, via a single-use, short-lived link sent to that e-mail address.',
        ),
        list(
          'The user is responsible for the security of their e-mail account and for all activity carried out with their sign-in link.',
          'Sign-in links must not be shared. If the user notices any misuse, they must report it without delay to hello@insurwiz.io.',
          'Individual templates and features are enabled by Insurwiz per account; the user has no entitlement to their availability.',
        ),
      ],
    },
    {
      heading: '4. Acceptable use',
      body: [
        p(
          'The Demo may be used solely to evaluate and demonstrate the service. The user undertakes to:',
        ),
        list(
          'enter only test data, their own data, or data of persons whose data they may lawfully use with an appropriate legal basis and information;',
          'not generate passes for persons who have not requested or do not expect them, and not use the Demo to send unsolicited messages;',
          'not upload unlawful, misleading or offensive content, or content infringing third-party rights (logos, text, images);',
          'not attempt to circumvent the Demo’s security measures, load-test it, use it in an automated or bulk manner, reverse-engineer it, or access other users’ data;',
          'not use the Demo in breach of applicable law.',
        ),
        p(
          'In case of a breach, Insurwiz may suspend or delete the account without prior notice and invalidate the passes created.',
        ),
      ],
    },
    {
      heading: '5. Passes and flight information',
      body: [
        p(
          'Passes created in the Demo serve demonstration purposes only. They do not constitute an insurance contract, policy, certificate of cover, ticket, right of admission or entitlement to any service, and they create no claim against Insurwiz, the partner or any third party.',
        ),
        p(
          'Flight information shown on passes and in notifications (departure, arrival, gate, terminal, delay, status) comes from external flight-data providers and is indicative only. It may be delayed, incomplete or inaccurate; travel decisions must be based solely on the official information of the airline and the airport.',
        ),
        p(
          'Demo passes are valid for a limited time: the pass record expires 14 days after creation, and the visibility window configured on the pass may be shorter. Expired or deleted passes may become inactive in the Wallet.',
        ),
      ],
    },
    {
      heading: '6. Third-party services',
      body: [
        p(
          'Passes are delivered and displayed through Apple Wallet (Apple Inc.) and Google Wallet (Google LLC), and push notifications are relayed by Apple’s and Google’s notification services. Their use is also subject to the respective provider’s own terms, and Insurwiz is not responsible for their operation, availability or changes.',
        ),
        p(
          'Insurwiz operates the Demo on Amazon Web Services (AWS) infrastructure in the European Union (Frankfurt, eu-central-1 region). Sign-in links are sent through an e-mail delivery service, and flight data is obtained from external flight-data providers.',
        ),
      ],
    },
    {
      heading: '7. Intellectual property and partner content',
      body: [
        p(
          'The Demo, its software, interface, templates, sample designs and documentation are the intellectual property of Insurwiz; use of the Demo transfers no rights in them. The user may use the Demo solely for evaluation under these terms.',
        ),
        p(
          'Logos, brand colours, texts and other content uploaded by the partner remain the property of the partner (or its licensor). By uploading, the partner warrants that it is entitled to use them and grants Insurwiz a non-exclusive, royalty-free licence to store and process the content, display it on passes and transmit it to Apple’s and Google’s services for the purpose of operating the Demo.',
        ),
        p(
          'Insurwiz may freely use feedback and suggestions about the Demo to improve the service, without compensation.',
        ),
      ],
    },
    {
      heading: '8. Data processing',
      body: [
        p(
          'To operate the Demo, Insurwiz processes the following data: the user’s e-mail address, name and company name; the partner’s brand settings (logo, colours, contact details, pass designs); recipient data entered for passes (name, e-mail address, flight number, date, departure and arrival airport and the other fields of the template); flight status data; and technical log data (IP address, timestamps, request identifiers) recorded for security and abuse prevention (for example rate limiting).',
        ),
        p(
          'For the user’s own data, Insurwiz is the data controller. For recipient data entered by the partner, the partner is the controller and Insurwiz acts as processor on the partner’s behalf for the purpose of operating the Demo; providing the necessary legal basis and information is the partner’s responsibility.',
        ),
        p(
          'Data is kept for as long as needed for the purpose of the Demo or until the account is closed; expired demo data is deleted regularly. The Demo is not suitable for, and must not be used to store, production customer data. To exercise data-subject rights (access, rectification, erasure, restriction, objection) and for any data-protection matter, contact gdpr@insurwiz.io.',
        ),
      ],
    },
    {
      heading: '9. Availability, changes and termination',
      body: [
        p(
          'The Demo is provided “as is” and “as available”. Insurwiz undertakes no obligation of continuous or error-free operation, any specific availability, the continued existence of features or the retention of data.',
        ),
        p(
          'Insurwiz may modify, suspend or discontinue the Demo or any part of it at any time without prior notice, delete accounts and invalidate passes. The user may request deletion of their account and data at any time at hello@insurwiz.io or gdpr@insurwiz.io.',
        ),
      ],
    },
    {
      heading: '10. Liability',
      body: [
        p(
          'Given the demonstration nature of the Demo, Insurwiz excludes — to the fullest extent permitted by law — liability for damage arising from the use or unavailability of the Demo, from the inaccuracy of information shown in it (including flight data) and from failures of third-party services, including loss of profit and consequential damage.',
        ),
        p(
          'This limitation does not apply to damage caused intentionally, to damage to human life, physical integrity or health, or to any case in which the exclusion or limitation of liability is not permitted by law.',
        ),
        p(
          'The user and the partner they represent are responsible for the data they enter and the content they upload, and for damage and claims arising from a breach of these terms.',
        ),
      ],
    },
    {
      heading: '11. Changes to these terms',
      body: [
        p(
          'Insurwiz may amend these terms at any time. The current version, together with its effective date, is always available on this page. Signing in or continuing to use the Demo after an amendment constitutes acceptance of the amended terms.',
        ),
      ],
    },
    {
      heading: '12. Governing law, language and contact',
      body: [
        p(
          'These terms are governed by Hungarian law. Disputes arising from them fall under the jurisdiction of the competent Hungarian courts. The terms are available in Hungarian and English; in case of any discrepancy, the Hungarian version prevails.',
        ),
        p(
          'For questions, comments or legal enquiries: hello@insurwiz.io (general), gdpr@insurwiz.io (data protection), legal@insurwiz.io (legal matters).',
        ),
      ],
    },
  ],
};

export const TERMS: Record<Lang, TermsDocument> = { hu, en };
